import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Heart, AlertTriangle, Users, Clock, Target } from 'lucide-react';

const ResponsibleGamingGuide = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Responsible Gaming Practices
            </h1>
            <p className="text-xl text-book-brown-dark leading-relaxed">
              Essential guidelines for maintaining control and ensuring gaming remains an enjoyable, sustainable activity.
            </p>
          </div>

          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our Guide Writing Philosophy</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p>Every guide published on Brit Ember represents extensive research and deep industry insights...</p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-6">The Foundation of Sustainable Gaming</h2>
            <p>Responsible gaming represents the cornerstone of legitimate casino entertainment in the UK...</p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Responsible Gaming Analytics</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">UK Gaming Statistics</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Problem Gamblers</span>
                    <span className="font-semibold text-red-600">0.6%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">At-Risk Players</span>
                    <span className="font-semibold text-orange-600">3.8%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Responsible Players</span>
                    <span className="font-semibold text-green-600">95.6%</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Self-Exclusion Usage</h3>
                <div className="flex justify-center mb-4">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#228B22" strokeWidth="20"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#FFD700" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="50.24"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#8B4513" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="87.96"/>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-book-brown">85%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Successful Recovery</span>
                    <span className="font-semibold">85%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Partial Success</span>
                    <span className="font-semibold">12%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Relapse Rate</span>
                    <span className="font-semibold">3%</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Gaming Session Patterns</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Healthy Sessions</span>
                    <div className="w-16 bg-green-500 rounded-full h-3"></div>
                    <span className="font-semibold text-green-600">72%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Extended Play</span>
                    <div className="w-12 bg-yellow-500 rounded-full h-3"></div>
                    <span className="font-semibold text-yellow-600">22%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-book-brown-dark">Problem Sessions</span>
                    <div className="w-6 bg-red-500 rounded-full h-3"></div>
                    <span className="font-semibold text-red-600">6%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
            <p>Responsible gaming represents the cornerstone of legitimate casino entertainment in the UK...</p>
            <div className="bg-book-beige rounded-lg p-6 my-8">
              <h4 className="text-xl font-bold text-book-brown mb-4">Essential Responsible Gaming Principles</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Set and respect predetermined limits for time and money</span>
                </li>
                <li className="flex items-start">
                  <Heart className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Recognize gaming as entertainment, not income generation</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Take regular breaks and avoid chasing losses</span>
                </li>
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Seek support when gaming impacts daily life</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResponsibleGamingGuide;
